// Prequalification funnel — CLIENT-SIDE indicative sizing.
//
// A TS mirror of the C# engine so the live meter updates instantly with no
// network call. DISPLAY-ONLY: float math here is an indicative preview; the
// C# engine (PrequalificationEngine.cs) remains authoritative at finalize,
// where the letter / deal-sheet figures come from. Keep the constants and
// rules below in lockstep with the server:
//   src/PhoenixCapital.Core/Constants/PrequalProgramTerms.cs   (source of truth)
//   src/PhoenixCapital.Core/Engine/Prequalification/PrequalificationEngine.cs

import type {
  PrequalBuyerAnswers,
  PrequalBuyerType,
  PrequalEngineResult,
  PrequalSellerAnswers,
} from "../../api/prequalification";
import { BUYER_KNOCKOUTS, SELLER_KNOCKOUTS } from "./prequalContent";

/** Mirror of PrequalProgramTerms.cs — the C# file is the source of truth.
 *  A SELECTIVE mirror: only the constants this client preview actually
 *  computes with are carried over. Notably ABSENT, deliberately:
 *  `DscrOriginationGate` (1.50x, the verified-figures underwriting gate). It
 *  sizes nothing here. The 1.50 this preview does use is `sizingCoverage`
 *  (T5(ii), on the Buyer Note Principal) — the figure prequal-v7 surfaces
 *  show. The 1.80/1.65 range DSCRs below serve the v6 mirror only. Do not bind
 *  a display to the origination gate. */
export const PREQUAL_PROGRAM_TERMS = {
  engineVersion: "prequal-v7",
  /** Annual contract interest rate as a FRACTION (0.10 = 10%). */
  interestRate: 0.1,
  amortizationMonths: 240,
  balloonMonth: 96,
  /** DSCR that sizes the LOW end of the seller's quoted dollar range.
   *  INVERTED relative to the dollars it produces: a HIGHER DSCR demands
   *  more coverage per dollar borrowed, so 1.80x supports the SMALLER note
   *  and makes the BOTTOM of the range (≈4.7974× annual cash flow at
   *  10%/240mo). */
  rangeLowNoteDscr: 1.8,
  /** DSCR that sizes the HIGH end — the LOWER DSCR supports the LARGER
   *  note and makes the TOP of the range (≈5.2336× annual cash flow). This
   *  is the coverage standard the public surfaces and the {{DscrFloor}}
   *  document token display. */
  rangeHighNoteDscr: 1.65,
  /** Down-payment band around the 5% midpoint. */
  downPaymentMidpoint: 0.05,
  downPaymentHighPct: 0.06,
  downPaymentLowPct: 0.04,
  /** The program minimum PURCHASE PRICE — $250,000 since 2026-10-16. Pinned
   *  to `PrequalProgramTerms.ProgramMinPrice` through
   *  tests/fixtures/prequal-parity-cases.json › programTerms, read by both
   *  suites. */
  programMinPrice: 250_000,
  programMaxPrice: 10_000_000,
  /** First-timer soft cap (ownership rung 1 AND management rung 1). */
  firstTimerCap: 1_000_000,

  // ── Seller two-phase economics (prequal-v4) ──────────────────────────────
  /** Days 0–90, while the seller holds the note. */
  sellerTransitionRate: 0.05,
  /** Day 90 onward, in the pool. */
  sellerPoolRate: 0.1,
  /** Illustrative vendor fee income on ORIGINAL FACE, on top of the pool rate.
   *  Variable in practice (2–4% disclosed); this is the illustration. */
  vendorFeeIncomeRate: 0.025,
  /** Length of the transition phase in months (90 days). */
  transitionMonths: 3,
  /** Hard ceiling on the FINANCED portion as a multiple of annual free cash
   *  flow. At rangeHighNoteDscr the derived note is ~5.2336x cash flow, so this
   *  clamp binds on EVERY deal and the top of every range is exactly 5.0x. */
  maxCashFlowMultiple: 5.0,

  // ── 1 November 2026 program terms (§2) — mirror of the C# block of the same
  // name. Pinned through tests/fixtures/prequal-parity-cases.json › programTerms
  // (both suites read it), so a one-sided edit fails the other side. The amount
  // financed is the part of the price the buyer finances, before any fee; the
  // Buyer Note Principal is the amount financed plus the setup fee.
  /** T3 — the 20-year amortization (240 months). */
  amortizationMonthsTwentyYear: 240,
  /** T3 — the 10-year amortization (120 months). */
  amortizationMonthsTenYear: 120,
  /** T1 — the program minimum, measured on the AMOUNT FINANCED. */
  minAmountFinanced: 250_000,
  /** T1 / T5(iii) — the program maximum, measured on the AMOUNT FINANCED. */
  maxAmountFinanced: 10_000_000,
  /** T5(ii) — sizing coverage on the Buyer Note Principal's P&I (no fee). */
  sizingCoverage: 1.5,
  /** T7 — the Origination Coverage Ratio floor (staff views only). */
  originationCoverageFloor: 1.35,
  /** T10 — Payment Administration Fee, a FRACTION of each scheduled payment. */
  buyerPaymentAdminFeePct: 0.0195,
  /** T9 — Buyer Onboarding and Setup Fee, a FRACTION of the amount financed. */
  buyerOnboardingSetupFeePct: 0.0495,
  /** T11 — the seller's Underwriting Fee (never on the buyer's note). */
  sellerUnderwritingFeePct: 0.0295,
  /** T13 — First-Loss Equity Reserve, at pool entry. */
  firstLossEquityReservePct: 0.05,
  /** T13 — the seller's one-time costs, "about 7.95%". */
  sellerOneTimeCostsPct: 0.0795,
  /** T14 — Pre-Contribution Administration Fee, per year of the balance. */
  preContributionAdministrationFeeAnnualPct: 0.05,
  /** T15 — Pool Administration Fee, per year of pool balances. */
  poolAdministrationFeeAnnualPct: 0.015,
  /** T16 — Processing and Closing Fee, paid by the note holder. */
  processingAndClosingFeePct: 0.01,
  /** T17 — premium = this share of remaining scheduled interest… */
  prepaymentPremiumInterestShare: 0.5,
  /** T17 — …through this month; none after. */
  prepaymentPremiumThroughMonth: 90,
  /** T18 — late charge per installment. */
  lateChargeAmount: 150,
  /** T18 — late once more than this many days past due. */
  lateChargeAfterDays: 10,
  /** T18 — default interest, simple, as a FRACTION. */
  defaultInterestRate: 0.15,
  /** T18 — assumption fee, of outstanding principal. */
  assumptionFeePct: 0.03,
  /** T18 — extension fee. */
  extensionFeePct: 0.01,
  /** T20 — personal guaranty from each owner holding this share or more. */
  personalGuarantyOwnershipThreshold: 0.2,

  // 🔴 RETIRED in prequal-v4: defaultSellerDownPaymentPct. Phoenix requires no
  // downpayment and sets no minimum, so there is no default to apply and no
  // band to resolve — price = note + down, where down is an optional amount
  // that may be zero.
} as const;

/**
 * Mirror of the NESTED `PrequalProgramTerms.CashEquivalentHaircuts` class —
 * the per-category multipliers applied to a buyer's non-cash down-payment
 * assets before they join `cashAvailable` in the sizing numerator.
 *
 * A SIBLING const rather than a key on PREQUAL_PROGRAM_TERMS above, because
 * the C# side is a nested static class pinned by its own dedicated test
 * (Cash_Equivalent_Haircuts_Are_Inert_And_Mirrored) rather than by the flat
 * constants test — so the two languages keep a 1:1 pin topology, and the
 * structured-table-gets-its-own-const shape already set by
 * PREQUAL_BUYER_FACTOR_LADDERS is followed.
 *
 * EVERY FACTOR IS 1.00 TODAY — no discount is applied to any category. The
 * seam exists so a future decision to count illiquid capital at less than face
 * value is a change to these five numbers (and the five C# constants) and
 * nothing else. Do NOT multiply a category inline at a call site:
 * `totalDownPaymentCapacity` is the single reader on this side.
 *
 * A one-sided edit is what the hard-coded table in prequalSizing.test.ts (and
 * its C# twin) catches — every parity fixture row sizes at 1.00, so none of
 * them would notice a factor lowered in only one language.
 */
export const PREQUAL_CASH_EQUIVALENT_HAIRCUTS = {
  realEstateEquity: 1.0,
  securities: 1.0,
  businessEquity: 1.0,
  /** Deliberately SEPARATE from `securities`: the two carry different access
   *  costs, so they must stay independently adjustable. */
  retirementAccounts: 1.0,
  otherAssets: 1.0,
} as const;

/**
 * The dollars this buyer can actually put toward a down payment: cash, plus
 * each named non-cash category at its liquidity haircut (all 1.00 today, so
 * today this is a plain sum). TS twin of
 * `PrequalBuyerAnswers.TotalDownPaymentCapacity()`.
 *
 * The SHORT-CIRCUIT is load-bearing, not an optimisation: with no equivalents
 * named this returns `cashAvailable` itself rather than summing five zeros, so
 * a cash-only buyer's arithmetic is byte-identical to what it was before this
 * existed. An absent category adds nothing — it is never read as a
 * zero-valued selection.
 */
export function totalDownPaymentCapacity(a: PrequalBuyerAnswers): number {
  const e = a.cashEquivalents;
  if (e === null || e === undefined) return a.cashAvailable;

  const h = PREQUAL_CASH_EQUIVALENT_HAIRCUTS;
  const equivalents =
    (e.realEstateEquity ?? 0) * h.realEstateEquity +
    (e.securities ?? 0) * h.securities +
    (e.businessEquity ?? 0) * h.businessEquity +
    (e.retirementAccounts ?? 0) * h.retirementAccounts +
    (e.otherAssets ?? 0) * h.otherAssets;

  return a.cashAvailable + equivalents;
}

/**
 * What the buyer is assumed to hand the seller at the closing table — the
 * client mirror of `PrequalBuyerAnswers.StatedCashAtClosing()` in
 * `PrequalificationDtos.cs`, and the ONLY figure the review step's assumption
 * sentence may name.
 *
 * 🔴 IT IS CASH, NEVER {@link totalDownPaymentCapacity}, AND THE TWO ARE
 * INDISTINGUISHABLE ON EVERY FIXTURE THIS SUITE HAD. Capacity sums real-estate
 * equity, securities, business equity and retirement accounts at their
 * haircuts; the sentence beside it says the money "goes to the seller at
 * closing", and a seller at a closing table receives cash, not a 401(k). The
 * server's own accessor carries that reasoning verbatim and says printing
 * capacity there "would state a transaction that cannot happen" — and this
 * screen printed capacity anyway, one step before submit, on the surface a
 * buyer is most likely to believe and least likely to question. The fixtures
 * could not see it because they all supply `cashAvailable` with no
 * `cashEquivalents`, where capacity short-circuits to the cash figure itself
 * and the two functions return the same number for every input the suite feeds
 * them.
 *
 * 🔴 ZERO ON THE INTERNAL ARM, so the caller renders the assumption without a
 * number there. That arm's Deal Sheet prints $0 cash at closing and a note
 * equal to the whole price, so naming a six-figure sum in a sentence about the
 * closing table would put two answers to one question on two surfaces minutes
 * apart. The sentence that renders instead — "whatever you put in goes to the
 * seller" — is true of them and claims no figure.
 *
 * Capacity is not wrong everywhere; it is wrong HERE. It remains the right
 * question for "has this buyer answered the capital step", which is the other
 * consumer and reads pledgeable assets on purpose.
 *
 * ⚠️ THE DOCUMENT CLAMPS THIS AND THIS DOES NOT: the Deal Sheet prints
 * `Min(StatedCashAtClosing(), price)`, so an all-cash buyer whose cash exceeds
 * their own named asking price sees the full figure here and the price there.
 * Mirroring the ACCESSOR rather than the call site is deliberate — the clamp is
 * a property of a sheet that has a price to clamp against, and the letter-only
 * arm has none.
 */
export function statedCashAtClosing(a: PrequalBuyerAnswers): number {
  // The retired "cash at closing" field. Always null on a new submission; a
  // resumed pre-retirement draft can still carry one, and a stated figure wins
  // on every arm exactly as it does on the server.
  const stated = a.targetDownPayment;
  if (stated !== null && stated !== undefined) return Math.max(0, stated);

  return a.documentPath === "InternalBuyer" ? 0 : Math.max(0, a.cashAvailable);
}


/** prequal-v3 buyer factor ladders — mirror of the PrequalProgramTerms.cs
 *  factor block (PROVISIONAL values, calibration later). Ordered ascending
 *  by rung (rung = index + 1); an unknown/legacy answer degrades to rung 1
 *  (conservative), EXCEPT reserves which degrades to its neutral 1.00 rung. */
export const PREQUAL_BUYER_FACTOR_LADDERS: Record<string, readonly (readonly [string, number])[]> = {
  ownership: [
    ["never-owned", 0.5],
    ["owned-smaller", 0.75],
    ["owned-comparable", 0.95],
    ["owned-multiple", 1.1],
  ],
  management: [
    ["none", 0.85],
    ["small-team", 0.95],
    ["department-pnl", 1.0],
    ["executive-pnl", 1.05],
  ],
  industry: [
    ["new", 0.65],
    ["adjacent", 0.85],
    ["direct", 0.95],
    ["direct-5plus", 1.0],
  ],
  /** Re-keyed off the consolidated active-management answer (prequal-v3.1):
   *  "operator" is the top rung; "oversight-manager"/"hands-off" are the
   *  non-operator (absentee) path. Rung 1 ("hands-off", 0.80) is the floor a
   *  surviving conditional absentee on the AAMA path sizes at; since
   *  prequal-v6 the designated-operator path sizes at the "operator" rung. */
  involvement: [
    ["hands-off", 0.8],
    ["oversight-manager", 0.95],
    ["operator", 1.0],
  ],
  reserves: [
    ["under-3mo", 0.95],
    ["3-6mo", 1.0],
    ["6mo-plus", 1.05],
  ],
};

export type PrequalExperienceTier = "None" | "Limited" | "Strong";

const BUYER_ABSENTEE_KEY = "buyer-absentee";
const MONTHS_PER_YEAR = 12;

function normalize(value: string | null | undefined): string {
  return (value ?? "").trim().toLowerCase();
}

function round2(value: number): number {
  return Math.round(value * 100) / 100;
}

interface ResolvedRung {
  rung: number;
  value: string;
  factor: number;
}

/** The involvement rung whose value is `value` — mirror of
 *  `PrequalificationEngine.InvolvementRung`. */
function involvementRung(value: string): ResolvedRung {
  const ladder = PREQUAL_BUYER_FACTOR_LADDERS.involvement;
  const i = ladder.findIndex(([v]) => v === value);
  if (i < 0) throw new Error(`The involvement ladder has no '${value}' rung.`);
  return { rung: i + 1, value: ladder[i][0], factor: ladder[i][1] };
}

/** Resolve an answer against an ordered factor ladder (rung = index + 1);
 *  an unknown/legacy/missing value degrades to `fallbackRung` and reports
 *  that rung's canonical value — mirrors PrequalificationEngine.ResolveRung. */
function resolveRung(
  ladder: readonly (readonly [string, number])[],
  answer: string | null | undefined,
  fallbackRung: number,
): ResolvedRung {
  const normalized = normalize(answer);
  for (let i = 0; i < ladder.length; i++) {
    if (ladder[i][0] === normalized) return { rung: i + 1, value: ladder[i][0], factor: ladder[i][1] };
  }
  const fallback = ladder[fallbackRung - 1];
  return { rung: fallbackRung, value: fallback[0], factor: fallback[1] };
}

/**
 * A company buyer TYPE (the type half of `BuyerAcquirer.IsCompanyBuyer`).
 *
 * ⚠️ Deliberately a local twin of `isCompanyBuyerType` in
 * `api/prequalification.ts`, not an import of it: this module is the sizing
 * mirror, and two dozen funnel tests replace that API module wholesale with
 * `vi.mock(...)`, which would leave an imported predicate `undefined` and every
 * sizing call throwing. Type-only imports from it are erased and safe.
 */
export function isCompanyTypeAnswer(type: PrequalBuyerType | null | undefined): boolean {
  return type === "CompanyOwnedOrLed" || type === "InvestmentGroupOrFund";
}

/** How the business will be run day to day, as the active-management screen
 *  reads the answers. Mirror of `PrequalOperatorPath`
 *  (Core/Engine/Prequalification/PrequalActiveManagement.cs). */
export type PrequalOperatorPath = "Operator" | "Aama" | "DesignatedOperator" | "Absent";

/**
 * Whether the buyer's full-time-operator follow-up meets the program's
 * requirement — the business managed full time for the first two years, some
 * offsite work permitted (D4, FT). Mirror of
 * `BuyerAcquirer.DesignatedOperatorSatisfied` under the CURRENT engine rules
 * (`PrequalEngineRules.Current`): this is the live preview, never a replay.
 *
 * - Involvement "operator": never.
 * - A company buyer (company TYPE and a non-blank company NAME), any other
 *   involvement: a general manager to be hired satisfies it with or without a
 *   name; a principal and existing management need a name; a designated person
 *   never (D3: a company keeps its three).
 * - Anyone else is an individual: only when the involvement is
 *   "oversight-manager". A general manager to be hired satisfies it; existing
 *   management and a designated person need a name; a principal never. A
 *   hands-off, blank or unknown involvement never does (D2: AAMA only).
 *
 * Fails closed. Pinned to the C# by the shared parity fixture.
 */
export function designatedOperatorSatisfied(a: PrequalBuyerAnswers): boolean {
  const involvement = normalize(a.involvement);
  if (involvement === "operator") return false;
  // `??` does not catch "" — trim and test truthiness (CLAUDE.md › Frontend).
  const named = (a.operatorName ?? "").trim() !== "";
  if (isCompanyTypeAnswer(a.buyerType) && (a.acquiringCompanyName ?? "").trim()) {
    switch (a.operatorArrangement) {
      case "HiredGeneralManager":
        return true;
      case "CompanyPrincipal":
      case "ExistingManagement":
        return named;
      default:
        return false;
    }
  }
  if (involvement !== "oversight-manager") return false;
  switch (a.operatorArrangement) {
    case "HiredGeneralManager":
      return true;
    case "ExistingManagement":
    case "DesignatedPerson":
      return named;
    default:
      return false;
  }
}

/** Mirror of `PrequalActiveManagement.Resolve` (current rules): the designated
 *  operator wins over AAMA when a buyer answered both (owner rulings Q5 for a
 *  company, Q1 for an individual). */
export function resolveOperatorPath(a: PrequalBuyerAnswers): PrequalOperatorPath {
  if (normalize(a.involvement) === "operator") return "Operator";
  if (designatedOperatorSatisfied(a)) return "DesignatedOperator";
  return a.aamaPath ? "Aama" : "Absent";
}

/** M1: a company buyer TYPE sizes on cash alone — its one capital answer is the
 *  expected equity contribution. Mirror of
 *  `PrequalActiveManagement.WithoutCompanyNonCashAssets`; keyed on the type,
 *  not the name, because the funnel asks the company name last. */
export function withoutCompanyNonCashAssets(a: PrequalBuyerAnswers): PrequalBuyerAnswers {
  return isCompanyTypeAnswer(a.buyerType) && a.cashEquivalents != null
    ? { ...a, cashEquivalents: null }
    : a;
}

export interface KnockoutEvaluation {
  knockoutReasons: string[];
  unsureFlags: string[];
}

/** TS mirror of PrequalKnockoutRiskDescriptions.cs — knockout key → short
 *  prose RISK description (the unfavorable fact raised by a No/Unsure
 *  answer). KnockoutReasons/UnsureFlags carry THESE, not the keys, since
 *  prequal-v2. Keep in byte-lockstep with the C# map (a test pins it). */
export const PREQUAL_KNOCKOUT_RISK_DESCRIPTIONS: Record<string, string> = {
  // Buyer (8)
  "buyer-bankruptcy": "Active personal bankruptcy reported",
  "buyer-ofac": "OFAC sanctions clearance not confirmed",
  "buyer-felony": "Felony conviction history reported",
  "buyer-other-debt": "Additional acquisition financing planned alongside this loan",
  "buyer-absentee": "Owner will not be actively involved in operations",
  "buyer-no-guarantee": "Personal guarantee not confirmed",
  "buyer-excluded-industry": "Target operates in an excluded industry",
  "buyer-phoenix-default": "Standing on existing Phoenix Capital obligations not confirmed",
  // Seller (9)
  "seller-no-authority": "Authority to sell not confirmed",
  "seller-surviving-debt": "Other financing is being used to fund this transaction.",
  "seller-bankruptcy": "Active bankruptcy proceeding reported",
  "seller-excluded-industry": "Business operates in an excluded industry",
  "seller-nontransferable-license": "Licenses or certifications may not transfer",
  "seller-tax-lien": "Unresolved tax liens reported",
  "seller-investigation": "Active regulatory investigation reported",
  "seller-not-going-concern": "Business is not an active going concern",
  "seller-franchisor-denied": "Franchisor transfer consent not available",
  // Engine-synthesized (not fit-check keys)
  "seller-no-cash-flow": "No positive cash flow reported",
  // prequal-v7 (ruling Q5 (a)): both payment schedules size the amount financed
  // under the program minimum. Figure-free (a decline carries no figure).
  "seller-below-program-minimum":
    "Business cash flow supports less than the program minimum amount financed on either payment schedule",
  // ⚠️ TRUE BUT INCOMPLETE SINCE R50 — the predicate now tests the business note
  // PLUS what the buyer can bring, so the sentence is never false and no longer
  // says the buyer's capital was counted. Deliberately NOT reworded here: it is
  // customer-facing copy R51 reserves. R54's pre-authored replacement and the
  // five-file change set are recorded at the C# map
  // (PrequalKnockoutRiskDescriptions) — change both tiers together or the
  // parity fixture fails.
  "internal-buyer-below-program-minimum":
    "Business cash flow supports less than the program minimum",
};

/** The risk description for a knockout key; falls back to the key itself
 *  for an unknown value (defensive — mirrors the C# Describe). */
export function describeKnockoutRisk(key: string): string {
  return PREQUAL_KNOCKOUT_RISK_DESCRIPTIONS[key] ?? key;
}

/** Knockout evaluation (prequal-v2 polarity: Yes is favorable) — any "no"
 *  declines, "unsure" flags, a MISSING answer is inert (the live compute runs
 *  mid-form with knockouts unanswered and must not decline). The buyer
 *  active-management (absentee) gate is NOT a fit-check key anymore — since
 *  prequal-v3.1 it lives in computeBuyerSizing, keyed on involvement/aamaPath.
 *  "not-a-franchise" (the franchise question's third option) is neither "no"
 *  nor "unsure" → passes with no flag. Emits the risk DESCRIPTIONS. Mirrors the
 *  fit-check loops in PrequalificationEngine.EvaluateBuyer/EvaluateSeller. */
export function evaluateKnockouts(
  keys: readonly string[],
  answers: Record<string, string>,
): KnockoutEvaluation {
  const knockoutReasons: string[] = [];
  const unsureFlags: string[] = [];

  for (const key of keys) {
    const answer = normalize(answers[key]);
    const description = describeKnockoutRisk(key);
    if (answer === "no") knockoutReasons.push(description);
    else if (answer === "unsure") unsureFlags.push(description);
  }

  return { knockoutReasons, unsureFlags };
}

/** Standard level-payment amortization — monthly payment on principal P at
 *  the annual rate over n months. The rate is a FRACTION (0.10 = 10%);
 *  `lib/amortization.ts` carries the twin that takes a PERCENT, hence the
 *  spelled-out name. */
export function monthlyPaymentFromAnnualRateFraction(
  principal: number,
  annualRate: number,
  months: number,
): number {
  if (principal <= 0 || months <= 0) return 0;
  const i = annualRate / MONTHS_PER_YEAR;
  if (i === 0) return principal / months;
  return (principal * i) / (1 - Math.pow(1 + i, -months));
}


/** Whole cents for a value already quantized to cents. Strips the binary
 *  residue `round2` leaves behind (2_398_717.69 * 100 is 239871768.99999997),
 *  so arithmetic on the result is exact integer arithmetic. */
function centsOf(value: number): number {
  return Math.round(value * 100);
}


const BUYER_KNOCKOUT_KEYS = BUYER_KNOCKOUTS.map((q) => q.key);
const SELLER_KNOCKOUT_KEYS = SELLER_KNOCKOUTS.map((q) => q.key);

/** Engine-synthesized soft-decline reason KEY for a seller with no positive
 *  cash flow (mirrors PrequalificationEngine.SellerNoCashFlowReason). The
 *  emitted reason is its risk description, like every knockout. */
export const SELLER_NO_CASH_FLOW_REASON = "seller-no-cash-flow";

/** Engine-synthesized soft-decline reason KEY for an internal buyer whose
 *  business supports less than the program minimum (mirrors
 *  PrequalificationEngine.InternalBelowProgramMinimumReason). The emitted
 *  reason is its risk description, like every knockout. */
export const INTERNAL_BELOW_PROGRAM_MINIMUM_REASON = "internal-buyer-below-program-minimum";

/** Engine-synthesized soft-decline reason KEY for a seller whose business
 *  supports less than the program minimum amount financed on BOTH payment
 *  schedules (prequal-v7; mirrors
 *  PrequalificationEngine.SellerBelowProgramMinimumReason). */
export const SELLER_BELOW_PROGRAM_MINIMUM_REASON = "seller-below-program-minimum";

/** Decimal places a stored coverage ratio carries (mirror of
 *  PrequalificationEngine.RatioPlaces). */
const RATIO_PLACES = 4;

function roundRatio(value: number): number {
  const scale = 10 ** RATIO_PLACES;
  return Math.round(value * scale) / scale;
}

/** The v7 capital clamp (D3, ruling Q2 interpretation A) — mirror of
 *  PrequalificationEngine.FinancedClamp: the figure's FINANCED part (what it
 *  finances over the buyer's own capacity) held to [minAmountFinanced,
 *  maxAmountFinanced], then the capacity added back. */
function financedClamp(value: number, capacity: number): number {
  const own = Math.max(0, capacity);
  const financed = Math.min(
    Math.max(value - own, PREQUAL_PROGRAM_TERMS.minAmountFinanced),
    PREQUAL_PROGRAM_TERMS.maxAmountFinanced,
  );
  return round2(own + financed);
}

/** The v7 two-schedule members (§2 T5–T7) — mirror of
 *  PrequalificationEngine.WithSchedules. 20 = the 240-month schedule, 10 = the
 *  120-month schedule. Money to the cent, ratios to four places. */
function withSchedules(
  result: PrequalEngineResult,
  freeCashFlow: number,
  down: number,
  statedFinanced: number | null,
): PrequalEngineResult {
  const cashFlow = Math.max(0, freeCashFlow);
  const ownDown = Math.max(0, down);
  const twenty = maximumFinancedAmount(cashFlow, PREQUAL_PROGRAM_TERMS.amortizationMonthsTwentyYear);
  const ten = maximumFinancedAmount(cashFlow, PREQUAL_PROGRAM_TERMS.amortizationMonthsTenYear);
  const min = PREQUAL_PROGRAM_TERMS.minAmountFinanced;
  const statedCoverage = (months: number): number | null =>
    statedFinanced != null && statedFinanced > 0
      ? roundRatio(originationCoverageFor(cashFlow, statedFinanced, months))
      : null;
  return {
    ...result,
    scheduleCashFlow: round2(cashFlow),
    maxFinanced20: twenty.maximum,
    maxFinanced10: ten.maximum,
    binding20: twenty.binding,
    binding10: ten.binding,
    priceAt20: round2(twenty.maximum + ownDown),
    priceAt10: round2(ten.maximum + ownDown),
    buyerNotePrincipal20: twenty.buyerNotePrincipal,
    buyerNotePrincipal10: ten.buyerNotePrincipal,
    sizingCoverage20: roundRatio(twenty.sizingCoverage),
    sizingCoverage10: roundRatio(ten.sizingCoverage),
    originationCoverage20: roundRatio(twenty.originationCoverage),
    originationCoverage10: roundRatio(ten.originationCoverage),
    belowMinimum20: twenty.maximum < min,
    belowMinimum10: ten.maximum < min,
    statedAmountFinanced: statedFinanced,
    statedWithinMaximum20: statedFinanced == null ? null : statedFinanced <= twenty.maximum,
    statedWithinMaximum10: statedFinanced == null ? null : statedFinanced <= ten.maximum,
    statedOriginationCoverage20: statedCoverage(PREQUAL_PROGRAM_TERMS.amortizationMonthsTwentyYear),
    statedOriginationCoverage10: statedCoverage(PREQUAL_PROGRAM_TERMS.amortizationMonthsTenYear),
  };
}

// Readiness flags are advisory server-side document furniture — the funnel
// renders none of them, so the client preview emits an empty list; the
// authoritative set is computed by the C# engine at finalize.

interface BuyerScreening {
  knockoutReasons: string[];
  unsureFlags: string[];
  conditionalAbsentee: boolean;
}

/**
 * The screening every buyer gets regardless of how their range is sized: the
 * active-management gate, then the fit-check sweep. Mirror of
 * `PrequalificationEngine.ScreenBuyer`.
 *
 * 🔴 **SHARED SO THE TWO ARMS CANNOT DRIFT.** The internal-buyer path changes
 * what a range is sized ON; it changes nothing about who is declined.
 * Bankruptcy, OFAC, felony history, additional acquisition financing,
 * full-time management, the personal guarantee, excluded industries and standing on
 * existing Phoenix obligations all still apply, in the same order — and the
 * order matters, because both lists are rendered to the applicant as prose.
 *
 * The conditional-absentee FLOOR is deliberately NOT applied here: it is a
 * factor-ladder consequence and only the capital arm has ladders. The flag
 * itself is returned so both arms can report it.
 */
function screenBuyer(a: PrequalBuyerAnswers): BuyerScreening {
  const knockoutReasons: string[] = [];
  const unsureFlags: string[] = [];

  // Active-management (absentee) gate — a non-operator involvement survives as
  // a conditional flag WITH the AAMA path, or with a designated full-time
  // operator (a company buyer's, D4, or an individual overseer's, FT); else a
  // soft decline.
  let conditionalAbsentee = false;
  if (normalize(a.involvement) !== "operator") {
    const description = describeKnockoutRisk(BUYER_ABSENTEE_KEY);
    if (a.aamaPath || designatedOperatorSatisfied(a)) {
      conditionalAbsentee = true;
      unsureFlags.push(description);
    } else {
      knockoutReasons.push(description);
    }
  }

  const ko = evaluateKnockouts(BUYER_KNOCKOUT_KEYS, a.knockouts);
  knockoutReasons.push(...ko.knockoutReasons);
  unsureFlags.push(...ko.unsureFlags);

  return { knockoutReasons, unsureFlags, conditionalAbsentee };
}

/**
 * Indicative buyer sizing — the ROUTER, mirroring
 * `PrequalificationEngine.EvaluateBuyer`. The branch lives at the public entry
 * point rather than at a caller, for the same reason it does on the server: an
 * arm a caller has to opt into is an arm a caller can forget.
 */
export function computeBuyerSizing(answers: PrequalBuyerAnswers): PrequalEngineResult {
  // M1: a company buyer's non-cash assets never size anything (mirror of the
  // first line of `PrequalificationEngine.EvaluateBuyer`).
  const a = withoutCompanyNonCashAssets(answers);
  return a.documentPath === "InternalBuyer"
    ? computeBuyerSizingOnBusiness(a)
    : computeBuyerSizingOnCapital(a);
}

/** The buyer's own capital sizes the range — every path but InternalBuyer.
 *  Mirror of `PrequalificationEngine.EvaluateBuyerOnCapital`. */
function computeBuyerSizingOnCapital(a: PrequalBuyerAnswers): PrequalEngineResult {
  // prequal-v3 factor rungs — unknown/legacy values degrade to rung 1
  // (conservative), except reserves which degrades to its neutral rung.
  const own = resolveRung(PREQUAL_BUYER_FACTOR_LADDERS.ownership, a.ownershipExperience, 1);
  const mgmt = resolveRung(PREQUAL_BUYER_FACTOR_LADDERS.management, a.managementExperience, 1);
  const ind = resolveRung(PREQUAL_BUYER_FACTOR_LADDERS.industry, a.industryFit, 1);
  let inv = resolveRung(PREQUAL_BUYER_FACTOR_LADDERS.involvement, a.involvement, 1);
  const res = resolveRung(PREQUAL_BUYER_FACTOR_LADDERS.reserves, a.reservesBand, 2);

  // Strong-experience profile drives the display tier ONLY (prequal-v3.1) —
  // the absentee gate below keys on involvement/aamaPath, not the rungs.
  const strongExperience = (own.rung >= 3 || mgmt.rung === 4) && ind.rung >= 3;
  const firstTimer = own.rung === 1 && mgmt.rung === 1;
  const tier: PrequalExperienceTier = strongExperience ? "Strong" : firstTimer ? "None" : "Limited";

  const { knockoutReasons, unsureFlags, conditionalAbsentee } = screenBuyer(a);

  // A surviving absentee sizes regardless of the stated plan: on the
  // designated-operator path (FT, prequal-v6) at the OPERATOR rung, on the AAMA
  // path at the most conservative rung. Both looked up by value.
  if (conditionalAbsentee) {
    inv = involvementRung(resolveOperatorPath(a) === "DesignatedOperator" ? "operator" : "hands-off");
  }

  const factors = [
    { key: "ownership", rung: own.value, factor: own.factor },
    { key: "management", rung: mgmt.value, factor: mgmt.factor },
    { key: "industry", rung: ind.value, factor: ind.factor },
    { key: "involvement", rung: inv.value, factor: inv.factor },
    { key: "reserves", rung: res.value, factor: res.factor },
  ];

  if (knockoutReasons.length > 0) {
    return {
      outcome: "SoftDecline",
      knockoutReasons,
      unsureFlags,
      experienceTier: tier,
      conditionalAbsentee,
      factors,
      readinessFlags: [],
      engineVersion: PREQUAL_PROGRAM_TERMS.engineVersion,
    };
  }

  const product = own.factor * mgmt.factor * ind.factor * inv.factor * res.factor;
  // The sizing numerator is the COMBINED capacity — cash plus every named
  // non-cash asset at its haircut — never `cashAvailable` alone. Every guard
  // below (the `down <= 0` floors, the note subtraction) keys on this figure,
  // so a buyer with no cash but real assets sizes on the assets rather than
  // dropping to the program floor.
  const down = totalDownPaymentCapacity(a);
  const base = down <= 0 ? 0 : round2((down / PREQUAL_PROGRAM_TERMS.downPaymentMidpoint) * product);
  const lowRaw = down <= 0 ? 0 : round2((down / PREQUAL_PROGRAM_TERMS.downPaymentHighPct) * product);
  const highRaw = down <= 0 ? 0 : round2((down / PREQUAL_PROGRAM_TERMS.downPaymentLowPct) * product);

  // prequal-v7 (D3, ruling Q2 interpretation A): the program range bounds the
  // AMOUNT FINANCED — each figure is the capacity plus its financed part held to
  // [minAmountFinanced, maxAmountFinanced]. In-range buyers are unchanged; only
  // the clamp edges move. Mirror of the engine's FinancedClamp.
  let headline = financedClamp(base, down);
  let low = financedClamp(lowRaw, down);
  let high = financedClamp(highRaw, down);

  // Measured BEFORE the clamp, mirroring the engine: afterwards a figure
  // sitting AT the cap cannot be told from one the cap moved there. Either side
  // can bind alone, so the test is a disjunction.
  const capBound =
    firstTimer &&
    (headline > PREQUAL_PROGRAM_TERMS.firstTimerCap ||
      high > PREQUAL_PROGRAM_TERMS.firstTimerCap);

  if (firstTimer) {
    headline = Math.min(headline, PREQUAL_PROGRAM_TERMS.firstTimerCap);
    high = Math.min(high, PREQUAL_PROGRAM_TERMS.firstTimerCap);
  }

  // Never inverted: low ≤ headline ≤ high.
  low = Math.min(low, headline);
  high = Math.max(high, headline);

  const note = round2(Math.max(0, headline - down));

  const capitalResult: PrequalEngineResult = {
    outcome: "Pass",
    knockoutReasons,
    unsureFlags,
    buyingPowerBase: base,
    buyingPowerLow: low,
    buyingPowerHigh: high,
    experienceTier: tier,
    experienceCeiling: firstTimer ? PREQUAL_PROGRAM_TERMS.firstTimerCap : null,
    qualifiedAmount: headline,
    noteAmount: note,
    conditionalAbsentee,
    factors,
    firstTimerCapApplied: firstTimer,
    firstTimerCapBound: capBound,
    readinessFlags: [],
    engineVersion: PREQUAL_PROGRAM_TERMS.engineVersion,
  };

  // v7, D2: on the DEAL path the named business's free cash flow sizes the
  // Maximum Financed Amount on both schedules beside the capital headline (which
  // D3 keeps on capital). Mirror of the engine.
  const namedCashFlow = a.targetAnnualCashFlow;
  if (namedCashFlow != null && namedCashFlow > 0) {
    const statedCash = statedCashAtClosing(a);
    const namedPrice = a.targetPurchasePrice;
    const statedFinanced =
      namedPrice != null && namedPrice > 0 ? round2(Math.max(0, namedPrice - statedCash)) : null;
    return withSchedules(capitalResult, namedCashFlow, statedCash, statedFinanced);
  }

  return capitalResult;
}

/**
 * The BUSINESS sizes the range — the internal-buyer path. Mirror of
 * `PrequalificationEngine.EvaluateBuyerOnBusiness`.
 *
 * A son buying his father's company, a manager buying from the owner, a
 * minority partner buying out the majority: Phoenix requires no down payment
 * and sets no minimum from them, so the thing that qualifies the deal is the
 * business's cash flow and not the buyer's bank balance. Nothing here reads
 * `cashAvailable`, `cashEquivalents`, the factor ladders or the first-timer
 * cap — and no surface on this path may print a cash-derived buying-power
 * figure, because none is computed.
 */
function computeBuyerSizingOnBusiness(a: PrequalBuyerAnswers): PrequalEngineResult {
  // Rungs are resolved for the DISPLAY tier only — nothing below sizes from
  // them. Involvement and reserves are deliberately not resolved: the tier does
  // not read them, and resolving a value nothing consumes is how a reader comes
  // to believe a ladder is in play here.
  const own = resolveRung(PREQUAL_BUYER_FACTOR_LADDERS.ownership, a.ownershipExperience, 1);
  const mgmt = resolveRung(PREQUAL_BUYER_FACTOR_LADDERS.management, a.managementExperience, 1);
  const ind = resolveRung(PREQUAL_BUYER_FACTOR_LADDERS.industry, a.industryFit, 1);

  const strongExperience = (own.rung >= 3 || mgmt.rung === 4) && ind.rung >= 3;
  const firstTimer = own.rung === 1 && mgmt.rung === 1;
  const tier: PrequalExperienceTier = strongExperience ? "Strong" : firstTimer ? "None" : "Limited";

  const { knockoutReasons, unsureFlags, conditionalAbsentee } = screenBuyer(a);

  if (knockoutReasons.length > 0) {
    return internalDecline(knockoutReasons, unsureFlags, tier, conditionalAbsentee);
  }

  // prequal-v7 (§2 T5, ruling Q4) — mirror of
  // PrequalificationEngine.EvaluateBuyerOnBusinessV7. The business sizes the
  // Maximum Financed Amount on BOTH schedules; the stated down payment (a blank
  // is $0: Phoenix requires none) is added to each to give the purchase figure.
  // One figure rule (M2, Q9): the headline is the stated capital plus the
  // 20-year Maximum, and the note is that 20-year Maximum. Declines iff the
  // 20-year Maximum is under the minimum amount financed; R50's eligibility
  // term has no meaning on a minimum measured on the amount financed, so it is
  // not read here (v6 rows replay it server-side). A blank cash flow — the live
  // meter runs while somebody is typing — sizes $0 and declines.
  const cashFlow = Math.max(0, a.targetAnnualCashFlow ?? 0);
  const down = Math.max(0, a.targetDownPayment ?? 0);
  const namedPrice = a.targetPurchasePrice;
  const statedFinanced =
    namedPrice != null && namedPrice > 0 ? round2(Math.max(0, namedPrice - down)) : null;

  const twenty = maximumFinancedAmount(cashFlow, PREQUAL_PROGRAM_TERMS.amortizationMonthsTwentyYear);
  if (twenty.maximum < PREQUAL_PROGRAM_TERMS.minAmountFinanced) {
    knockoutReasons.push(describeKnockoutRisk(INTERNAL_BELOW_PROGRAM_MINIMUM_REASON));
    return internalDecline(knockoutReasons, unsureFlags, tier, conditionalAbsentee);
  }

  const ten = maximumFinancedAmount(cashFlow, PREQUAL_PROGRAM_TERMS.amortizationMonthsTenYear);
  const price20 = round2(twenty.maximum + down);
  const price10 = round2(ten.maximum + down);
  // A 10-year schedule below the minimum is never quoted as the low end of a
  // range: the range collapses to the one figure that clears it.
  const low = ten.maximum < PREQUAL_PROGRAM_TERMS.minAmountFinanced ? price20 : price10;

  return withSchedules(
    {
      outcome: "Pass",
      knockoutReasons,
      unsureFlags,
      buyingPowerBase: price20,
      buyingPowerLow: low,
      buyingPowerHigh: price20,
      experienceTier: tier,
      experienceCeiling: null,
      qualifiedAmount: price20,
      noteAmount: twenty.maximum,
      conditionalAbsentee,
      factors: [],
      firstTimerCapApplied: false,
      firstTimerCapBound: false,
      supportablePriceLow: low,
      supportablePriceHigh: price20,
      readinessFlags: [],
      engineVersion: PREQUAL_PROGRAM_TERMS.engineVersion,
    },
    cashFlow,
    down,
    statedFinanced,
  );
}

/** A declined internal buyer — mirror of
 *  `PrequalificationEngine.InternalDecline`. No figures (sized on nothing, so
 *  there is nothing to state) and an EMPTY factor list rather than an absent
 *  one, matching the pass arm so no consumer has to tell the two apart. */
function internalDecline(
  knockoutReasons: string[],
  unsureFlags: string[],
  tier: PrequalExperienceTier,
  conditionalAbsentee: boolean,
): PrequalEngineResult {
  return {
    outcome: "SoftDecline",
    knockoutReasons,
    unsureFlags,
    experienceTier: tier,
    conditionalAbsentee,
    factors: [],
    firstTimerCapApplied: false,
    firstTimerCapBound: false,
    readinessFlags: [],
    engineVersion: PREQUAL_PROGRAM_TERMS.engineVersion,
  };
}

// ── Deal Explorer math (§6) ─────────────────────────────────────────────
// TS mirrors of the C# AmortizationMath the deal-sheet renderer uses
// (Core/Engine/Payments/AmortizationMath.cs — same cent quantization,
// AwayFromZero-equivalent for positive money). DISPLAY-ONLY like the rest
// of this module; the server renders the authoritative deal-sheet PDF.

/** One month of interest on `balance` at `annualRate` (a FRACTION),
 *  quantized to cents — mirror of AmortizationMath.MonthlyInterest. */
export function monthlyInterest(balance: number, annualRate: number): number {
  if (balance <= 0 || annualRate <= 0) return 0;
  return round2((balance * annualRate) / MONTHS_PER_YEAR);
}

/** Split one level payment into (interest, principal) against the current
 *  balance — mirror of AmortizationMath.SplitPayment. */
export function splitPayment(
  balance: number,
  annualRate: number,
  amount: number,
): { interest: number; principal: number } {
  if (amount <= 0) return { interest: 0, principal: 0 };
  const interest = Math.min(monthlyInterest(balance, annualRate), amount);
  const principal = Math.min(round2(amount - interest), Math.max(balance, 0));
  return { interest, principal };
}

/** Year-1 principal + interest totals — twelve SplitPayment iterations,
 *  matching the deal-sheet renderer's month-by-month schedule. */
export function yearOneTotals(
  principal: number,
  annualRate: number,
  payment: number,
): { principal: number; interest: number } {
  let balance = principal;
  let totalPrincipal = 0;
  let totalInterest = 0;
  for (let month = 0; month < MONTHS_PER_YEAR; month++) {
    const split = splitPayment(balance, annualRate, payment);
    totalInterest += split.interest;
    totalPrincipal += split.principal;
    balance = round2(balance - split.principal);
  }
  return { principal: round2(totalPrincipal), interest: round2(totalInterest) };
}

/** Outstanding balance due at the balloon after `balloonMonths` level
 *  payments — mirror of AmortizationMath.BalloonBalance (closed form,
 *  zero-rate degrades to straight-line, floored at zero). */
export function balloonBalance(
  principal: number,
  annualRate: number,
  payment: number,
  balloonMonths: number,
): number {
  if (principal <= 0 || balloonMonths <= 0) return 0;
  if (annualRate <= 0) return round2(Math.max(0, principal - payment * balloonMonths));
  const r = annualRate / MONTHS_PER_YEAR;
  const factor = Math.pow(1 + r, balloonMonths);
  return round2(Math.max(0, principal * factor - payment * ((factor - 1) / r)));
}

/** One month of the amortization schedule. `balance` is the balance AFTER the
 *  month's payment is applied. */
export interface ScheduleRow {
  month: number;
  payment: number;
  interest: number;
  principal: number;
  balance: number;
}

/**
 * Month-by-month schedule from month 1 through MATURITY (the program balloon
 * month, 96) — the TS twin of the loop in `PrequalDealSheetHtmlRenderer
 * .RenderBuyer`, iterating the same quantized `splitPayment` so the on-screen
 * table and the downloaded PDF agree row for row.
 *
 * 🔴 The final row's `balance` is the ITERATED month-96 balance and is
 * deliberately NOT `balloonBalance()`. The two differ by cents — the iterated
 * path quantizes interest every month, the closed form does not — and the
 * figure a reader can add up from the rows above has to be the one that is
 * right.
 *
 * ⚠️ THIS PARAGRAPH USED TO END "`balloonBalance()` stays the source for the
 * standalone balloon caption, exactly as the C# renderer keeps it for its own
 * footnote." That was FALSE about C# and it is how the divergence survived:
 * `PrequalDealSheetHtmlRenderer.RenderBuyer` takes the SCHEDULE's terminal
 * balance for its caption too (`AmortizationScheduleTerminalBalance`), and its
 * own comment says so. So the browser preview quoted one balloon and the PDF it
 * generated quoted another — 4c apart on a $2.5M note, agreeing only after
 * whole-dollar rounding, which is why nobody ever saw two numbers.
 * `computeDealScenario` now reads this schedule, so the caption, the table and
 * the PDF are one figure by construction. `balloonBalance()` remains the mirror
 * of `AmortizationMath.BalloonBalance` and keeps its own direct tests.
 *
 * Stops early if the balance ever reaches zero. Unreachable at the program's
 * 10% rate over a 240-month amortization; it exists so a future zero/low rate
 * degrades to a short honest table rather than a tail of $0 rows.
 */
export function buildSchedule(
  principal: number,
  annualRate: number,
  payment: number,
  months: number = PREQUAL_PROGRAM_TERMS.balloonMonth,
): ScheduleRow[] {
  if (principal <= 0 || payment <= 0 || months <= 0) return [];
  const out: ScheduleRow[] = [];
  let balance = principal;
  for (let month = 1; month <= months; month++) {
    const split = splitPayment(balance, annualRate, payment);
    balance = round2(balance - split.principal);
    out.push({
      month,
      payment,
      interest: split.interest,
      principal: split.principal,
      balance,
    });
    if (balance <= 0) break;
  }
  return out;
}

export interface DealScenarioInputs {
  purchasePrice: number;
  downPayment: number;
  annualCashFlow: number;
}

export interface DealScenarioResult {
  /** Note = purchase price − down payment (floored at 0). */
  noteAmount: number;
  monthlyPayment: number;
  annualDebtService: number;
  /** annualCashFlow / annualDebtService; null when there is no debt service. */
  dscr: number | null;
  /** Coverage at or above the 1.65x sizing standard the program quotes
   *  against. NOT a floor and NOT an approval test — a scenario below it is
   *  simply one the program would size smaller. */
  meetsSizingStandard: boolean;
  year1Principal: number;
  year1Interest: number;
  /** annualCashFlow − annualDebtService. */
  year1Profit: number;
  /** year1Profit / downPayment (a fraction); null when down payment is 0. */
  cashOnCash: number | null;
  /** (year1Profit + year1Principal) / downPayment; null when down payment is 0. */
  totalFirstYearReturn: number | null;
  /** Balance due at the program balloon (month 96 / year 8). */
  balloonBalance: number;
}

/** Full Deal Explorer scenario at program terms (rate/amortization/balloon
 *  from PREQUAL_PROGRAM_TERMS — rates are FRACTIONS, 0.10 = 10%). */
export function computeDealScenario(inputs: DealScenarioInputs): DealScenarioResult {
  const note = round2(Math.max(0, inputs.purchasePrice - inputs.downPayment));
  const payment = round2(
    monthlyPaymentFromAnnualRateFraction(
      note,
      PREQUAL_PROGRAM_TERMS.interestRate,
      PREQUAL_PROGRAM_TERMS.amortizationMonths,
    ),
  );
  const annualDebtService = round2(payment * MONTHS_PER_YEAR);
  const dscr = annualDebtService > 0 ? inputs.annualCashFlow / annualDebtService : null;
  const year1 = yearOneTotals(note, PREQUAL_PROGRAM_TERMS.interestRate, payment);
  const year1Profit = round2(inputs.annualCashFlow - annualDebtService);
  const down = inputs.downPayment;
  return {
    noteAmount: note,
    monthlyPayment: payment,
    annualDebtService,
    dscr,
    meetsSizingStandard: dscr !== null && meetsCoverageStandard(dscr),
    year1Principal: year1.principal,
    year1Interest: year1.interest,
    year1Profit,
    cashOnCash: down > 0 ? year1Profit / down : null,
    totalFirstYearReturn: down > 0 ? (year1Profit + year1.principal) / down : null,
    // 🔴 THE SCHEDULE'S OWN TERMINAL BALANCE, not the closed form — one
    // predicate for a figure this page renders twice and the generated PDF
    // renders a third time. See the note on `buildSchedule`.
    balloonBalance: scheduleTerminalBalance(
      note,
      PREQUAL_PROGRAM_TERMS.interestRate,
      payment,
      PREQUAL_PROGRAM_TERMS.balloonMonth,
    ),
  };
}

/** The balance left after `months` quantized periods — the last row of
 *  `buildSchedule`, named so a caller that wants only the figure does not have
 *  to know that a schedule was walked to get it. Mirror of
 *  `AmortizationScheduleTerminalBalance` on the C# side, which both the buyer
 *  deal sheet and the staff answer panel read. */
export function scheduleTerminalBalance(
  principal: number,
  annualRate: number,
  payment: number,
  months: number = PREQUAL_PROGRAM_TERMS.balloonMonth,
): number {
  const rows = buildSchedule(principal, annualRate, payment, months);
  return rows.length > 0 ? rows[rows.length - 1].balance : 0;
}

// 🔴 RETIRED in prequal-v4, mirroring the C# engine: the band→percent map,
// resolveSellerDownPaymentPct, resolveDownPayment and downPaymentAtPrice.
//
// All four served the two-mode down-payment model, whose percent arm derived
// price as `note ÷ (1 − pct)`. Phoenix requires no downpayment and sets no
// minimum, so there is nothing to band and nothing to default: the seller
// states one optional amount that may be zero, and `price = note + down`
// holds unconditionally. The percent-mode guards that resolveDownPayment and
// downPaymentAtPrice had to keep identical cannot arise from a dollar figure
// floored at zero — the divergence class is gone rather than guarded.

/** The down payment as a FRACTION of price — DERIVED for display only, never
 *  an input. Mirror of SellerProgramEconomics.DerivedDownPaymentPercent. */
function derivedDownPaymentPercent(note: number, down: number): number {
  const d = Math.max(0, down);
  const price = Math.max(0, note) + d;
  return price > 0 ? d / price : 0;
}


/** The FINANCED multiple — note ÷ cash flow. Deliberately not a price
 *  multiple: the cap is on the financed portion. Mirror of
 *  SellerProgramEconomics.FinancedMultiple. */
function financedMultiple(note: number, annualCashFlow: number): number {
  return annualCashFlow <= 0 ? 0 : round2(note / annualCashFlow);
}

/**
 * Whether a coverage ratio is at or above the program's coverage standard.
 *
 * 🔴 The MIRROR of PrequalCoverageVerdict.Meets, and the reason both exist:
 * `ratio >= 1.65` was written three times — here, the buyer deal sheet's
 * meets/below suffix, and the staff answer panel's coverage sentence — in
 * three files across two languages. One rule about where a customer-facing
 * verdict turns should be one expression per language, and the shared parity
 * fixture is what holds these two in step.
 *
 * It is `rangeHighNoteDscr` (1.65), the DSCR the TOP of every quoted dollar
 * range is SIZED at — never the lower origination gate, which describes
 * nothing an applicant is quoted against.
 */
export function meetsCoverageStandard(ratio: number): boolean {
  return ratio >= PREQUAL_PROGRAM_TERMS.rangeHighNoteDscr;
}

/** The seller result, widened with the range/down-payment members the C#
 *  PrequalEngineResult carries. Assignable to PrequalEngineResult, so
 *  callers typed against the wire DTO are unaffected. */
export interface PrequalSellerSizing extends PrequalEngineResult {
  /** BOTTOM of the dollar range — the note sized at the HIGHER 1.80x DSCR. */
  noteLow?: number | null;
  /** TOP of the dollar range — the note sized at the LOWER 1.65x DSCR. */
  noteHigh?: number | null;
  /** Mean of the two. */
  noteMid?: number | null;
  /** Down payment as a FRACTION of price — DERIVED for display only. */
  downPaymentPercent?: number | null;
  /** The down payment in DOLLARS, as the seller stated it. May be zero. */
  downPaymentAmount?: number | null;
  /** Note ÷ cash flow — the FINANCED multiple, capped at 5.0x. */
  financedMultiple?: number | null;
  /** Computed coverage at the note the range's TOP prints, after the clamp. */
  coverageAtNote?: number | null;
  /** Days 0–90 monthly. */
  transitionMonthly?: number | null;
  /** Day 90 onward monthly — pool P&I plus vendor fee income. */
  poolMonthly?: number | null;
  /** Down + 3 transition payments + 9 pool distributions. CASH ONLY. */
  firstYearCash?: number | null;
}

export function computeSellerSizing(a: PrequalSellerAnswers): PrequalSellerSizing {
  const { knockoutReasons, unsureFlags } = evaluateKnockouts(SELLER_KNOCKOUT_KEYS, a.knockouts);
  const cashFlow = a.annualCashFlow;
  if (cashFlow <= 0) knockoutReasons.push(describeKnockoutRisk(SELLER_NO_CASH_FLOW_REASON));

  if (knockoutReasons.length > 0) {
    return {
      outcome: "SoftDecline",
      knockoutReasons,
      unsureFlags,
      readinessFlags: [],
      engineVersion: PREQUAL_PROGRAM_TERMS.engineVersion,
    };
  }

  // prequal-v7 (§2 T1, T5–T7, ruling Q5 (a)) — mirror of
  // PrequalificationEngine.EvaluateSellerV7. The Maximum Financed Amount on BOTH
  // schedules; the price per schedule is that Maximum plus the stated down
  // payment (optional, zero allowed). Below the minimum on the 10-year schedule
  // alone: a pass with that schedule flagged. Below it on BOTH: a soft decline
  // carrying no figure. The two-phase receipts are not computed (D7 removes
  // them from every seller surface).
  const twenty = maximumFinancedAmount(cashFlow, PREQUAL_PROGRAM_TERMS.amortizationMonthsTwentyYear);
  if (twenty.maximum < PREQUAL_PROGRAM_TERMS.minAmountFinanced) {
    return {
      outcome: "SoftDecline",
      knockoutReasons: [...knockoutReasons, describeKnockoutRisk(SELLER_BELOW_PROGRAM_MINIMUM_REASON)],
      unsureFlags,
      readinessFlags: [],
      engineVersion: PREQUAL_PROGRAM_TERMS.engineVersion,
    };
  }

  const ten = maximumFinancedAmount(cashFlow, PREQUAL_PROGRAM_TERMS.amortizationMonthsTenYear);
  const down = Math.max(0, a.downPaymentAmount ?? 0);
  const statedFinanced =
    a.askingPrice != null && a.askingPrice > 0 ? round2(Math.max(0, a.askingPrice - down)) : null;
  const price20 = round2(twenty.maximum + down);
  const price10 = round2(ten.maximum + down);
  const low = ten.maximum < PREQUAL_PROGRAM_TERMS.minAmountFinanced ? price20 : price10;

  return withSchedules(
    {
      outcome: "Pass",
      knockoutReasons,
      unsureFlags,
      // The v1–v6 note members, re-pointed to the two schedules (mirror of the
      // engine): the 10-year Maximum is the low, the 20-year Maximum the high and
      // the headline.
      noteLow: ten.maximum,
      noteHigh: twenty.maximum,
      noteMid: twenty.maximum,
      maxNote: twenty.maximum,
      conservativeNote: ten.maximum,
      supportablePriceLow: low,
      supportablePriceHigh: price20,
      // Ruling Q10: no ratio at the asking price.
      coverageAtAsking: null,
      downPaymentPercent: derivedDownPaymentPercent(twenty.maximum, down),
      downPaymentAmount: round2(down),
      sellerDownPaymentPct: derivedDownPaymentPercent(twenty.maximum, down),
      financedMultiple: financedMultiple(twenty.maximum, cashFlow),
      coverageAtNote: roundRatio(twenty.sizingCoverage),
      readinessFlags: [],
      engineVersion: PREQUAL_PROGRAM_TERMS.engineVersion,
    },
    cashFlow,
    down,
    statedFinanced,
  );
}

// ── Program financing (1 November 2026 terms) ──────────────────────────────
//
// Mirror of Core/Engine/Prequalification/ProgramFinancing.cs: the Maximum
// Financed Amount (§2 T5), the coverage ratios (T5(ii), T7) and the buyer's
// payments (T2–T4, T9, T10). CLOSED FORM on UNROUNDED values; only displayed
// figures are rounded to the cent (§2 "Rounding"). Unlike the v1…v6 seller
// sizing above, nothing here bisects over quantized payments, so the float
// closed form and the C# decimal closed form agree to the cent without the
// integer-cents tie trick. Pinned to the §2 golden values G1–G8 by
// tests/fixtures/prequal-parity-cases.json › financingCases, read by both
// suites.

/** Which §2 T5 limit set the Maximum Financed Amount. */
export type ProgramFinancingLimit =
  | "FiveTimesCashFlow"
  | "CoverageOnBuyerNotePrincipal"
  | "ProgramMaximum";

/** The buyer's payments on one schedule — mirror of the C# `ProgramPayments`. */
export interface ProgramPayments {
  amortizationMonths: number;
  amountFinanced: number;
  setupFee: number;
  buyerNotePrincipal: number;
  monthlyPrincipalAndInterest: number;
  monthlyPaymentAdministrationFee: number;
  monthlyTotal: number;
  balloon: number;
  twelveMonthPrincipalAndInterest: number;
  twelveMonthPaymentAdministrationFee: number;
  monthlyPrincipalAndInterestUnrounded: number;
}

/** Mirror of the C# `MaximumFinancedAmountResult`. The two ratios are
 *  UNROUNDED; money members are rounded to the cent. */
export interface MaximumFinancedAmountResult {
  freeCashFlow: number;
  amortizationMonths: number;
  limitFiveTimes: number;
  limitCoverage: number;
  limitProgramMaximum: number;
  maximum: number;
  binding: ProgramFinancingLimit;
  buyerNotePrincipal: number;
  twelveMonthPrincipalAndInterest: number;
  twelveMonthPaymentAdministrationFee: number;
  sizingCoverage: number;
  originationCoverage: number;
  belowMinimum: boolean;
  payments: ProgramPayments;
}

/** The two schedules a buyer may elect, 20-year first (the default). */
export const PROGRAM_SCHEDULE_MONTHS = [
  PREQUAL_PROGRAM_TERMS.amortizationMonthsTwentyYear,
  PREQUAL_PROGRAM_TERMS.amortizationMonthsTenYear,
] as const;

/** True for 240 or 120 months. */
export function isProgramSchedule(amortizationMonths: number): boolean {
  return (
    amortizationMonths === PREQUAL_PROGRAM_TERMS.amortizationMonthsTwentyYear ||
    amortizationMonths === PREQUAL_PROGRAM_TERMS.amortizationMonthsTenYear
  );
}

function requireProgramSchedule(amortizationMonths: number): void {
  if (!isProgramSchedule(amortizationMonths)) {
    throw new RangeError(
      `amortizationMonths must be 240 (20-year) or 120 (10-year); got ${amortizationMonths}.`,
    );
  }
}

function requireNonNegative(name: string, value: number): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new RangeError(`${name} must be a non-negative number; got ${value}.`);
  }
}

/** Round a NON-NEGATIVE amount × rate to the cent through integer cents, so an
 *  exact half-cent rounds up (C# `MoneyMath.Quantize`, AwayFromZero) instead
 *  of landing on a binary residue just below the tie. `rateTimesTenThousand`
 *  is the rate × 10,000 (4.95% → 495, 1.95% → 195). */
function centsTimesRate(amount: number, rateTimesTenThousand: number): number {
  return Math.round((centsOf(amount) * rateTimesTenThousand) / 10_000) / 100;
}

const SETUP_FEE_X10K = Math.round(PREQUAL_PROGRAM_TERMS.buyerOnboardingSetupFeePct * 10_000);
const PAF_X10K = Math.round(PREQUAL_PROGRAM_TERMS.buyerPaymentAdminFeePct * 10_000);
const UW_FEE_X10K = Math.round(PREQUAL_PROGRAM_TERMS.sellerUnderwritingFeePct * 10_000);

/** The level-payment factor at the program rate: payment = principal × factor. */
function programPaymentFactor(amortizationMonths: number): number {
  const r = PREQUAL_PROGRAM_TERMS.interestRate / MONTHS_PER_YEAR;
  const growth = Math.pow(1 + r, amortizationMonths);
  return (r * growth) / (growth - 1);
}

function buildProgramPayments(
  amountFinanced: number,
  setupFee: number,
  amortizationMonths: number,
): ProgramPayments {
  const principal = round2(amountFinanced + setupFee);
  const rawPayment = principal > 0 ? principal * programPaymentFactor(amortizationMonths) : 0;
  const payment = round2(rawPayment);
  const fee = centsTimesRate(payment, PAF_X10K);
  const twelve = MONTHS_PER_YEAR * rawPayment;
  const r = PREQUAL_PROGRAM_TERMS.interestRate / MONTHS_PER_YEAR;
  const growth = Math.pow(1 + r, PREQUAL_PROGRAM_TERMS.balloonMonth);
  const balance = principal > 0 ? principal * growth - rawPayment * ((growth - 1) / r) : 0;
  return {
    amortizationMonths,
    amountFinanced,
    setupFee,
    buyerNotePrincipal: principal,
    monthlyPrincipalAndInterest: payment,
    monthlyPaymentAdministrationFee: fee,
    monthlyTotal: (centsOf(payment) + centsOf(fee)) / 100,
    balloon: round2(Math.max(0, balance)),
    twelveMonthPrincipalAndInterest: round2(twelve),
    twelveMonthPaymentAdministrationFee: round2(twelve * PREQUAL_PROGRAM_TERMS.buyerPaymentAdminFeePct),
    monthlyPrincipalAndInterestUnrounded: rawPayment,
  };
}

/** The buyer's payments when `amountFinanced` is financed on one schedule: the
 *  setup fee is added to principal, P&I is paid on the Buyer Note Principal,
 *  and the Payment Administration Fee is debited with each payment (G1, G2). */
export function programPayments(amountFinanced: number, amortizationMonths: number): ProgramPayments {
  requireNonNegative("amountFinanced", amountFinanced);
  requireProgramSchedule(amortizationMonths);
  return buildProgramPayments(
    amountFinanced,
    centsTimesRate(amountFinanced, SETUP_FEE_X10K),
    amortizationMonths,
  );
}

/** Payments on a principal with NO setup fee — the brochure's generic
 *  "$1 million loan" illustration (G3). Never a buyer's own deal. */
export function paymentsOnPrincipal(principal: number, amortizationMonths: number): ProgramPayments {
  requireNonNegative("principal", principal);
  requireProgramSchedule(amortizationMonths);
  return buildProgramPayments(principal, 0, amortizationMonths);
}

/** The Maximum Financed Amount on one schedule: the least of (i) 5 × free cash
 *  flow, (ii) the amount which, increased by the setup fee, has twelve months
 *  of P&I covered 1.50× by free cash flow, and (iii) $10,000,000. On a tie,
 *  five times cash flow wins, then the program maximum. */
export function maximumFinancedAmount(
  freeCashFlow: number,
  amortizationMonths: number,
): MaximumFinancedAmountResult {
  requireNonNegative("freeCashFlow", freeCashFlow);
  requireProgramSchedule(amortizationMonths);
  const t = PREQUAL_PROGRAM_TERMS;
  const limitFive = t.maxCashFlowMultiple * freeCashFlow;
  const limitCoverage =
    freeCashFlow /
    t.sizingCoverage /
    (MONTHS_PER_YEAR * programPaymentFactor(amortizationMonths)) /
    (1 + t.buyerOnboardingSetupFeePct);
  const limitMax = t.maxAmountFinanced;

  let binding: ProgramFinancingLimit;
  let least: number;
  if (limitFive <= limitCoverage && limitFive <= limitMax) {
    binding = "FiveTimesCashFlow";
    least = limitFive;
  } else if (limitMax <= limitCoverage) {
    binding = "ProgramMaximum";
    least = limitMax;
  } else {
    binding = "CoverageOnBuyerNotePrincipal";
    least = limitCoverage;
  }

  const maximum = round2(least);
  const payments = programPayments(maximum, amortizationMonths);
  const twelve = MONTHS_PER_YEAR * payments.monthlyPrincipalAndInterestUnrounded;
  const twelveFee = twelve * t.buyerPaymentAdminFeePct;
  return {
    freeCashFlow,
    amortizationMonths,
    limitFiveTimes: round2(limitFive),
    limitCoverage: round2(limitCoverage),
    limitProgramMaximum: limitMax,
    maximum,
    binding,
    buyerNotePrincipal: payments.buyerNotePrincipal,
    twelveMonthPrincipalAndInterest: payments.twelveMonthPrincipalAndInterest,
    twelveMonthPaymentAdministrationFee: payments.twelveMonthPaymentAdministrationFee,
    sizingCoverage: twelve > 0 ? freeCashFlow / twelve : 0,
    originationCoverage: twelve > 0 ? freeCashFlow / (twelve + twelveFee) : 0,
    belowMinimum: maximum < t.minAmountFinanced,
    payments,
  };
}

/** T5(ii) sizing coverage an entered amount financed carries. Unrounded. */
export function sizingCoverageFor(
  freeCashFlow: number,
  amountFinanced: number,
  amortizationMonths: number,
): number {
  const p = programPayments(amountFinanced, amortizationMonths);
  const twelve = MONTHS_PER_YEAR * p.monthlyPrincipalAndInterestUnrounded;
  return twelve > 0 ? freeCashFlow / twelve : 0;
}

/** T7 Origination Coverage Ratio an entered amount financed carries
 *  (staff views only). Unrounded. */
export function originationCoverageFor(
  freeCashFlow: number,
  amountFinanced: number,
  amortizationMonths: number,
): number {
  const p = programPayments(amountFinanced, amortizationMonths);
  const twelve = MONTHS_PER_YEAR * p.monthlyPrincipalAndInterestUnrounded;
  const fee = twelve * PREQUAL_PROGRAM_TERMS.buyerPaymentAdminFeePct;
  return twelve > 0 ? freeCashFlow / (twelve + fee) : 0;
}

/** The seller's Underwriting Fee on an amount financed (T11, G8). */
export function sellerUnderwritingFee(amountFinanced: number): number {
  requireNonNegative("amountFinanced", amountFinanced);
  return centsTimesRate(amountFinanced, UW_FEE_X10K);
}

// ── Deal Explorer at the 1 November 2026 terms (M39) ──────────────────────
//
// The Explorer models one schedule at a time — the buyer's 10/20-year picker,
// defaulting to 20 (D5). Every figure follows D4: the 4.95% Buyer Onboarding and
// Setup Fee is added to the amount financed, P&I is paid on that Buyer Note
// Principal at 10%, the 1.95% Payment Administration Fee is debited with each
// payment, and the balloon is the closed-form balance after 96 payments on the
// UNROUNDED payment (ruling Q1). The page displays cents (Q1: screens show cents).

/** One schedule's scenario at program terms. */
export interface ProgramDealScenarioInputs {
  purchasePrice: number;
  downPayment: number;
  annualCashFlow: number;
  /** 240 (20-year, the default) or 120 (10-year). */
  amortizationMonths: number;
}

export interface ProgramDealScenarioResult {
  amortizationMonths: number;
  /** Price less the down payment — the amount financed (never below zero). */
  amountFinanced: number;
  payments: ProgramPayments;
  /** Twelve months of the total monthly debit (P&I plus the fee). */
  annualDebtService: number;
  /** Free cash flow ÷ twelve months' P&I on the Buyer Note Principal (the T5(ii)
   *  basis, excluding the fee); null when there is no payment. Unrounded. */
  sizingCoverage: number | null;
  /** The sizing coverage is at least the program's 1.50 sizing standard. */
  meetsSizingStandard: boolean;
  /** Principal repaid over the first twelve payments (unrounded schedule). */
  year1Principal: number;
  /** annualCashFlow − annualDebtService. */
  year1Profit: number;
  /** year1Profit / downPayment (a fraction); null when the down payment is 0. */
  cashOnCash: number | null;
  /** (year1Profit + year1Principal) / downPayment; null when the down payment is 0. */
  totalFirstYearReturn: number | null;
  /** The Maximum Financed Amount this free cash flow supports on this schedule. */
  maximumFinanced: number;
  /** The scenario's amount financed is at or under that Maximum. */
  withinMaximum: boolean;
  /** The amount financed is outside the $250,000–$10,000,000 program range. */
  outsideProgramRange: boolean;
}

/** One row of the Explorer's schedule: computed UNROUNDED, displayed rounded, so
 *  the last row's balance is exactly the closed-form balloon (ruling Q1). */
export interface ProgramScheduleRow {
  month: number;
  principalAndInterest: number;
  paymentAdministrationFee: number;
  interest: number;
  principal: number;
  balance: number;
}

/** The Explorer's scenario on one schedule (M39). */
export function computeProgramDealScenario(inputs: ProgramDealScenarioInputs): ProgramDealScenarioResult {
  requireProgramSchedule(inputs.amortizationMonths);
  const down = Math.max(0, inputs.downPayment);
  const amountFinanced = round2(Math.max(0, inputs.purchasePrice - down));
  const payments = programPayments(amountFinanced, inputs.amortizationMonths);
  const annualDebtService = round2(MONTHS_PER_YEAR * payments.monthlyTotal);
  const twelvePandI = MONTHS_PER_YEAR * payments.monthlyPrincipalAndInterestUnrounded;
  const sizingCoverage = twelvePandI > 0 ? inputs.annualCashFlow / twelvePandI : null;
  const rows = buildProgramSchedule(payments, MONTHS_PER_YEAR);
  const year1Principal = round2(
    payments.buyerNotePrincipal - (rows.length > 0 ? rows[rows.length - 1].balanceUnrounded : 0),
  );
  const year1Profit = round2(inputs.annualCashFlow - annualDebtService);
  const maximum = maximumFinancedAmount(Math.max(0, inputs.annualCashFlow), inputs.amortizationMonths);
  const t = PREQUAL_PROGRAM_TERMS;
  return {
    amortizationMonths: inputs.amortizationMonths,
    amountFinanced,
    payments,
    annualDebtService,
    sizingCoverage,
    meetsSizingStandard: sizingCoverage !== null && sizingCoverage >= t.sizingCoverage,
    year1Principal,
    year1Profit,
    cashOnCash: down > 0 ? year1Profit / down : null,
    totalFirstYearReturn: down > 0 ? (year1Profit + year1Principal) / down : null,
    maximumFinanced: maximum.maximum,
    withinMaximum: amountFinanced <= maximum.maximum,
    outsideProgramRange:
      amountFinanced < t.minAmountFinanced || amountFinanced > t.maxAmountFinanced,
  };
}

/** The month-by-month schedule on the Buyer Note Principal through the balloon
 *  month, iterated on the UNROUNDED payment and displayed rounded. */
export function buildProgramSchedule(
  payments: ProgramPayments,
  months: number = PREQUAL_PROGRAM_TERMS.balloonMonth,
): (ProgramScheduleRow & { balanceUnrounded: number })[] {
  const r = PREQUAL_PROGRAM_TERMS.interestRate / MONTHS_PER_YEAR;
  const raw = payments.monthlyPrincipalAndInterestUnrounded;
  let balance = payments.buyerNotePrincipal;
  const rows: (ProgramScheduleRow & { balanceUnrounded: number })[] = [];
  for (let month = 1; month <= months && balance > 0; month++) {
    const interest = balance * r;
    const principal = raw - interest;
    balance = balance - principal;
    rows.push({
      month,
      principalAndInterest: payments.monthlyPrincipalAndInterest,
      paymentAdministrationFee: payments.monthlyPaymentAdministrationFee,
      interest: round2(interest),
      principal: round2(principal),
      balance: round2(Math.max(0, balance)),
      balanceUnrounded: Math.max(0, balance),
    });
  }
  // The last row IS the balloon (ruling Q1): pin it to the closed form so the
  // table and the caption can never print two figures for one balance.
  if (rows.length === PREQUAL_PROGRAM_TERMS.balloonMonth) {
    rows[rows.length - 1].balance = payments.balloon;
  }
  return rows;
}
